import { useUserStore } from '@/store/user';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Daybook } from '../api/getDaybookList';
import { decrypt } from '@/utils/decrypt';

import Quill from 'quill';
import 'quill/dist/quill.bubble.css';
import clsx from 'clsx';
import i18n from '@/localisation/i18n';
import { setReadId, SubmitSetReadId } from '@/api/setReadId';
import dayjs from 'dayjs';
import { getDaybookVisualConfig } from './DaybookTypeConfig';

import CustomImage from 'quill/formats/image';

class CustomImageBlot extends CustomImage {
  static create(value: string | { url: string }) {
    const node = super.create(typeof value === 'string' ? value : value.url);

    node.setAttribute(
      'style',
      'max-width: 800px; ' +
        'height: auto; ' +
        'border-radius: 20px; ' +
        'display: block; ' +
        'width: 100%; ' +
        'margin: 20px 0;'
    );

    return node;
  }
}

Quill.register('formats/image', CustomImageBlot, true);

export const ItemDaybook: FC<Daybook & { client: string }> = data => {
  const { addDaybookView, clients } = useUserStore();
  const quillRef = useRef<HTMLDivElement>(null);

  const dataKey = `${data.client}:${data.id}`;
  const [decryptedEntry, setDecryptedEntry] = useState<{
    key: string;
    text: string;
  } | null>(null);

  // Пока расшифровка в полёте (или ключ устарел) — показываем доступный
  // plaintext-fallback, а не пустоту
  const displayText =
    decryptedEntry?.key === dataKey ? decryptedEntry.text : data.text ?? null;

  useEffect(() => {
    let ignore = false;

    const handleDecryption = async () => {
      let text = data.text ?? '';

      if (data.textBin) {
        try {
          text = await decrypt(data.textBin, data.client);
        } catch (error) {
          Sentry.captureException(error);
        }
      }

      if (!ignore) setDecryptedEntry({ key: dataKey, text });
    };

    handleDecryption();

    return () => {
      ignore = true;
    };
  }, [data.text, data.textBin, data.client, dataKey]);

  useEffect(() => {
    if (quillRef.current && displayText) {
      quillRef.current.innerHTML = '';
      const quill = new Quill(quillRef.current, {
        readOnly: true,
        theme: 'bubble',
        modules: {
          clipboard: false,
          toolbar: false,
          history: false,
          scroll: false,
          keyboard: true,
          uploader: false,
        },
      });

      // Устанавливаем данные в редактор
      try {
        quill.setContents(JSON.parse(displayText));
      } catch {
        quill.setText(displayText);
      }
    }
  }, [displayText]);

  const { mutate } = useMutation({
    mutationFn: (data: SubmitSetReadId) => setReadId(data),
    onSuccess: () => {
      addDaybookView(data.client!, data.id);
    },
  });

  const daybooksViewed = clients[data.client!]?.daybooksViewed;
  const isRead = useMemo(() => {
    return daybooksViewed?.has(data.id);
  }, [data.id, daybooksViewed]);

  const setRead = () => {
    if (!isRead) {
      mutate({ user: data.client!, id: data.id });
    }
  };

  const config = useMemo(
    () => getDaybookVisualConfig(data.typeDaybook),
    [data.typeDaybook]
  );

  const formattedTime = useMemo(() => {
    if (!data.creationDate) return null;
    const d = dayjs(data.creationDate);
    return d.isValid() ? d.format('HH:mm') : null;
  }, [data.creationDate]);

  return (
    <Link
      className={clsx(
        'group relative flex min-h-[170px] max-h-[240px] flex-col justify-between overflow-hidden rounded-lg border p-4',
        'transition-all duration-200 ease-out hover:-translate-y-0.5',
        config.cardBgClass,
        config.cardBorderClass
      )}
      onClick={setRead}
      to={`${data.id}`}
    >
      {/* Фоновый силуэт иконки */}
      <div className="pointer-events-none absolute -bottom-2 -right-2 select-none opacity-20 transition-transform duration-300 ease-out group-hover:scale-105">
        {config.watermarkIcon}
      </div>

      {/* Верхняя строка: Бейдж типа + Время + Статус (Новая / Просмотрено) */}
      <div className="relative z-10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={clsx(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
              config.badgeClass
            )}
          >
            {config.icon}
            <span>{config.title}</span>
          </div>

          {formattedTime && (
            <span
              className={clsx(
                'text-xs opacity-75',
                config.timeClass
              )}
            >
              {formattedTime}
            </span>
          )}
        </div>

        <div
          className={clsx(
            'badge badge-sm shrink-0 border text-xs transition-colors',
            isRead
              ? 'badge-outline border-additional/40 text-additional font-normal'
              : 'border-orange bg-orange text-main font-semibold'
          )}
        >
          {i18n.t(isRead ? 'viewed' : 'new')}
        </div>
      </div>

      {/* Контент: превью Quill */}
      <div className="relative z-10 my-3 flex-1 overflow-hidden">
        <div
          className="pointer-events-none line-clamp-4 text-sm leading-relaxed text-white/85 [&_.ql-container]:!border-0 [&_.ql-editor]:!p-0 [&_.ql-editor]:!text-sm [&_.ql-editor]:!text-white/85 [&_.ql-editor]:!leading-relaxed"
          ref={quillRef}
        />
      </div>

      {/* Нижняя строка: Индикатор перехода */}
      <div className="relative z-10 mt-auto flex items-center justify-between pt-1 text-xs">
        <span className="text-additional/60 group-hover:text-additional transition-colors flex items-center gap-1">
          <span>Подробнее</span>
          <svg
            className="h-3.5 w-3.5 transform transition-transform duration-200 group-hover:translate-x-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
};
